import React, { useEffect, useRef, useState } from 'react';
import './emerge-landing.css';
import WebsiteInquiryForm from './WebsiteInquiryForm';
import { useLandingHead } from './landingHead';

/*
 * The public site. Served at https://portal.emergesc.com/ AND, through
 * CloudFront, at https://emergesc.com/ (2026-10-06; the WordPress site is
 * retired). On emergesc.com only `/`, the static assets and the inquiry POST
 * reach the portal host; every other path is redirected to the portal. So:
 *
 *   - every link stays on this page (anchors) or is absolute;
 *   - a link into the portal app uses PORTAL_URL, never a relative path;
 *   - the page makes no API request on load. The one request it makes is the
 *     visitor's own form submission, same-origin.
 */

const IMG = (name: string) => `/emerge/${name}`;

/** The portal app, absolute: a relative link would 301 away from emergesc.com. */
export const PORTAL_URL = 'https://portal.emergesc.com';

const LOGO = IMG('emerge-senior-care-logo-300.png');
const LOGO_2X = IMG('emerge-senior-care-logo-768.png');

function Logo({ height }: { height: number }) {
  // The files are 300x213 and 768x545: the width follows the height at the logo's ratio.
  return (
    <img
      className="brand__logo"
      src={LOGO}
      srcSet={`${LOGO} 1x, ${LOGO_2X} 2x`}
      width={Math.round((height * 300) / 213)}
      height={height}
      alt="eMerge Senior Care"
      decoding="async"
    />
  );
}

function ArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function EmergeLanding() {
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useLandingHead();

  // https://emergesc.com/#contact opened directly: the browser looked for the
  // anchor before the SPA rendered it, so scroll to it once it exists.
  useEffect(() => {
    let id = '';
    try { id = decodeURIComponent((window.location.hash || '').slice(1)); } catch { return undefined; }
    if (!id) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const target = document.getElementById(id);
      if (target && typeof target.scrollIntoView === 'function') target.scrollIntoView({ block: 'start' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const revealEls = root.querySelectorAll('.reveal');
    if (typeof IntersectionObserver === 'undefined') {
      revealEls.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    revealEls.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="emerge-landing" ref={rootRef}>
      <a className="skip-link" href="#main">Skip to main content</a>

      <div className="rename-banner" role="note" aria-label="Our name">
        <div className="rename-banner__inner">
          <span className="rename-banner__mark" aria-hidden="true"></span>
          <span>Formerly Sounds for Seniors</span>
        </div>
      </div>

      <div className="topbar" aria-label="Contact information">
        <div className="topbar__inner">
          <span>On-site specialty care designed for long-term care communities.</span>
          <div className="topbar__meta">
            <a href="tel:+18137752200">813-775-2200</a>
            <a href="mailto:info@emergesc.com">info@emergesc.com</a>
          </div>
        </div>
      </div>

      <header className="site-header">
        <nav className="nav" aria-label="Primary navigation">
          <a className="brand" href="#top">
            <Logo height={64} />
          </a>

          <div className={`nav__links${menuOpen ? ' is-open' : ''}`} id="navLinks">
            <a href="#why" onClick={closeMenu}>Why eMerge</a>
            <a href="#services" onClick={closeMenu}>Services</a>
            <a href="#process" onClick={closeMenu}>How it works</a>
            <a href="#settings" onClick={closeMenu}>Who we serve</a>
            <a href="#contact" onClick={closeMenu}>Contact</a>
          </div>

          <div className="nav__actions">
            <a className="btn btn--primary" href="#contact">
              Partner with eMerge <ArrowIcon />
            </a>
            <button
              className="menu-toggle"
              id="menuToggle"
              aria-expanded={menuOpen}
              aria-controls="navLinks"
              aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              onClick={() => setMenuOpen((o) => !o)}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth={2} strokeLinecap="round" /></svg>
            </button>
          </div>
        </nav>
      </header>

      <main id="main">
        <section className="hero" id="top">
          <div className="hero__inner">
            <div className="hero__copy reveal">
              <span className="eyebrow">Where care connects</span>
              <h1>Specialty care, delivered <span>where residents live.</span></h1>
              <p className="hero__lead">eMerge brings Dentistry, Podiatry, Optometry, and Audiology directly to long-term care communities&mdash;making access easier for residents and coordination simpler for facility teams.</p>
              <div className="hero__actions">
                <a className="btn btn--primary" href="#contact">
                  Start a partnership inquiry <ArrowIcon />
                </a>
                <a className="btn btn--ghost" href="#services">Explore services</a>
              </div>
              <div className="hero__proof" aria-label="Key benefits">
                <span className="proof-item"><span className="proof-icon">&#10003;</span> On-site care</span>
                <span className="proof-item"><span className="proof-icon">&#10003;</span> Facility-friendly coordination</span>
                <span className="proof-item"><span className="proof-icon">&#10003;</span> Resident-centered experience</span>
              </div>
            </div>

            <div className="hero-visual reveal" aria-label="Senior care environment">
              <div className="hero-photo">
                <img src={IMG('2173110193.jpg')} alt="A welcoming senior living environment" />
              </div>
              <div className="hero-card hero-card--top">
                <div className="hero-card__label">One trusted partner</div>
                <div className="hero-card__title">Four on-site specialties</div>
                <div className="hero-card__copy">A coordinated care model built around residents, families, and facility teams.</div>
                <div className="specialty-dots" aria-hidden="true"><span></span><span></span><span></span><span></span></div>
              </div>
              <div className="hero-card hero-card--bottom">
                <div className="hero-card__label">Built for long-term care</div>
                <div className="hero-card__title">Less transport. More continuity.</div>
                <div className="hero-card__copy">Care comes to the community, reducing disruption and logistical complexity.</div>
              </div>
            </div>
          </div>
        </section>

        <div className="trust-strip" aria-label="Care settings served">
          <div className="trust-strip__inner">
            <div className="trust-strip__label">Designed for:</div>
            <div className="trust-item">Assisted living</div>
            <div className="trust-item">Memory care</div>
            <div className="trust-item">Skilled nursing</div>
            <div className="trust-item">CCRCs</div>
            <div className="trust-item">Senior communities</div>
          </div>
        </div>

        <section className="section" id="why">
          <div className="container value-layout">
            <div className="value-image reveal">
              <img src={IMG('1468437321.jpg')} alt="A care professional reviewing referral information" />
              <div className="value-image__note">
                <strong>Built around your community</strong>
                We coordinate with your routines, staff, and care plans so specialty access feels integrated&mdash;not disruptive.
              </div>
            </div>

            <div>
              <div className="section-heading reveal">
                <span className="eyebrow">Built for facilities</span>
                <h2>Specialty care that fits long-term care realities.</h2>
                <p>eMerge helps communities expand resident access to specialty care without adding unnecessary transportation, scheduling, or coordination burden.</p>
              </div>
              <div className="value-grid">
                <article className="value-card reveal">
                  <div className="value-card__icon">01</div>
                  <h3>Care comes on-site</h3>
                  <p>Residents receive specialty services in a familiar setting, helping reduce stress and off-site transport complexity.</p>
                </article>
                <article className="value-card reveal">
                  <div className="value-card__icon">02</div>
                  <h3>Clear coordination</h3>
                  <p>Facility teams receive responsive communication, straightforward scheduling, and practical follow-through.</p>
                </article>
                <article className="value-card reveal">
                  <div className="value-card__icon">03</div>
                  <h3>Resident-first care</h3>
                  <p>Every interaction is designed around dignity, comfort, safety, and the unique needs of older adults.</p>
                </article>
                <article className="value-card reveal">
                  <div className="value-card__icon">04</div>
                  <h3>One coordinated partner</h3>
                  <p>Four specialties under one relationship make it easier to expand access while maintaining continuity.</p>
                </article>
              </div>
            </div>
          </div>
        </section>

        <section className="section section--soft" id="services">
          <div className="container">
            <div className="section-heading section-heading--center reveal">
              <span className="eyebrow">Our services</span>
              <h2>Four specialties. One commitment to care.</h2>
              <p>Comprehensive on-site services designed to support comfort, mobility, communication, vision, nutrition, and quality of life.</p>
            </div>

            <div className="services-grid">
              <article className="service-card reveal" id="dentistry">
                <img src={IMG('qtq80-j0YVBE-1024x683.jpeg')} alt="Dentistry care in a clinical setting" />
                <div className="service-card__content">
                  <span className="service-card__number">01 &middot; On-site</span>
                  <h3>Dentistry</h3>
                  <p>Oral care that supports comfort, nutrition, confidence, and whole-body health.</p>
                  <a href="#contact">Ask about dentistry <span aria-hidden="true">&rarr;</span></a>
                </div>
              </article>

              <article className="service-card reveal" id="podiatry">
                <img src={IMG('2181979363-1024x683.jpg')} alt="Podiatry care for an older adult" />
                <div className="service-card__content">
                  <span className="service-card__number">02 &middot; On-site</span>
                  <h3>Podiatry</h3>
                  <p>Foot and ankle care that supports mobility, comfort, and safer daily living.</p>
                  <a href="#contact">Ask about podiatry <span aria-hidden="true">&rarr;</span></a>
                </div>
              </article>

              <article className="service-card reveal" id="optometry">
                <img src={IMG('2151920926-3.jpg')} alt="Clinician providing care to a senior resident" />
                <div className="service-card__content">
                  <span className="service-card__number">03 &middot; On-site</span>
                  <h3>Optometry</h3>
                  <p>Vision care delivered with a calm, coordinated experience for residents and staff.</p>
                  <a href="#contact">Ask about optometry <span aria-hidden="true">&rarr;</span></a>
                </div>
              </article>

              <article className="service-card reveal" id="audiology">
                <img src={IMG('qtq80-v6CYhL-1024x683.jpeg')} alt="Audiology care for an older adult" />
                <div className="service-card__content">
                  <span className="service-card__number">04 &middot; On-site</span>
                  <h3>Audiology</h3>
                  <p>Hearing care that supports communication, connection, safety, and quality of life.</p>
                  <a href="#contact">Ask about audiology <span aria-hidden="true">&rarr;</span></a>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="section section--dark" id="process">
          <div className="container">
            <div className="section-heading reveal">
              <span className="eyebrow" style={{ color: '#7bd8ca' }}>A simpler care model</span>
              <h2>From first conversation to on-site care.</h2>
              <p>eMerge works with facility teams to establish the right service mix, coordinate visits, and maintain clear communication around ongoing care.</p>
            </div>
            <div className="process-grid">
              <article className="process-card reveal">
                <div className="process-card__step">1</div>
                <h3>Tell us about your community</h3>
                <p>We learn about your setting, resident population, specialty needs, operating routines, and care coordination priorities.</p>
              </article>
              <article className="process-card reveal">
                <div className="process-card__step">2</div>
                <h3>Build the right care plan</h3>
                <p>Our team aligns scheduling, referral workflows, communication expectations, and the specialties available in your location.</p>
              </article>
              <article className="process-card reveal">
                <div className="process-card__step">3</div>
                <h3>Deliver and coordinate on-site</h3>
                <p>Specialists come to your community, document clearly, communicate with your team, and coordinate follow-up.</p>
              </article>
            </div>
          </div>
        </section>

        <section className="section" id="settings">
          <div className="container outcomes">
            <div className="section-heading reveal">
              <span className="eyebrow">Care that works for everyone</span>
              <h2>Better access for residents. Simpler coordination for teams.</h2>
              <p>eMerge is designed to create a more dependable experience for the people receiving care and the people responsible for coordinating it.</p>
            </div>
            <div className="outcome-list">
              <article className="outcome-item reveal">
                <div className="outcome-item__icon">R</div>
                <div><h3>For residents</h3><p>Care in a familiar environment with less travel, fewer disruptions, and an experience centered on dignity and comfort.</p></div>
              </article>
              <article className="outcome-item reveal">
                <div className="outcome-item__icon">F</div>
                <div><h3>For facility teams</h3><p>A dependable partner that supports scheduling, referrals, documentation, and responsive communication.</p></div>
              </article>
              <article className="outcome-item reveal">
                <div className="outcome-item__icon">&hearts;</div>
                <div><h3>For families</h3><p>Greater reassurance that essential specialty care is available where their loved one already lives.</p></div>
              </article>
              <article className="outcome-item reveal">
                <div className="outcome-item__icon">C</div>
                <div><h3>For care continuity</h3><p>Specialty services coordinated around the broader care environment rather than delivered in isolation.</p></div>
              </article>
            </div>
          </div>
          <div className="container settings-wrap" aria-label="Care environments">
            <div className="setting-pill">Assisted living</div>
            <div className="setting-pill">Memory care</div>
            <div className="setting-pill">Skilled nursing</div>
            <div className="setting-pill">Post-acute &amp; rehab</div>
            <div className="setting-pill">Community-based senior care</div>
          </div>
        </section>

        <section className="section section--soft">
          <div className="container">
            <div className="cta-panel reveal">
              <div className="cta-panel__content">
                <span className="eyebrow" style={{ color: '#7bd8ca' }}>Bring specialty care on-site</span>
                <h2>A better care experience can start with one conversation.</h2>
                <p>Tell us about your community and the services your residents need. The eMerge team will help you understand availability, next steps, and how an on-site partnership can work.</p>
                <a className="btn btn--light" href="#contact">
                  Connect with eMerge <ArrowIcon />
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="section" id="contact">
          <div className="container contact-layout">
            <div className="contact-card reveal">
              <span className="eyebrow">Contact &amp; referral form</span>
              <h3>Choose the right next step.</h3>
              <p>Whether you represent a facility, have a question about resident care, or want to learn more about eMerge, our team will route your inquiry appropriately.</p>
              <WebsiteInquiryForm />
            </div>

            <div className="contact-card contact-card--dark reveal">
              <span className="eyebrow" style={{ color: '#7bd8ca' }}>Contact eMerge</span>
              <h3>We&rsquo;re ready to listen.</h3>
              <p>Reach the team directly, or send the contact and referral form for the fastest routing.</p>
              <div className="contact-list">
                <div className="contact-row">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.9.33 1.78.62 2.62a2 2 0 0 1-.45 2.11L8 9.73a16 16 0 0 0 6 6l1.28-1.28a2 2 0 0 1 2.11-.45c.84.29 1.72.5 2.62.62A2 2 0 0 1 22 16.92Z" stroke="currentColor" strokeWidth={1.8} /></svg>
                  <div><strong>Phone</strong><a href="tel:+18137752200">813-775-2200</a></div>
                </div>
                <div className="contact-row">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m4 4 8 7 8-7M4 4h16v16H4V4Z" stroke="currentColor" strokeWidth={1.8} strokeLinejoin="round" /></svg>
                  <div><strong>Email</strong><a href="mailto:info@emergesc.com">info@emergesc.com</a></div>
                </div>
                <div className="contact-row">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z" stroke="currentColor" strokeWidth={1.8} /><circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth={1.8} /></svg>
                  <div><strong>Office</strong><span>3000 Bayport Drive, Suite 980<br />Tampa, FL 33607</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="footer__main">
          <div className="footer__brand">
            <a className="brand brand--chip" href="#top">
              <Logo height={72} />
            </a>
            <p>On-site Dentistry, Podiatry, Optometry, and Audiology for long-term care communities&mdash;delivered where residents live. Formerly Sounds for Seniors.</p>
          </div>
          <div>
            <h4>Services</h4>
            <ul>
              <li><a href="#dentistry">Dentistry</a></li>
              <li><a href="#podiatry">Podiatry</a></li>
              <li><a href="#optometry">Optometry</a></li>
              <li><a href="#audiology">Audiology</a></li>
            </ul>
          </div>
          <div>
            <h4>Company</h4>
            <ul>
              <li><a href="#why">About</a></li>
              <li><a href="#contact">Contact</a></li>
              <li><a href="https://www.linkedin.com/company/emerge-senior-care/" rel="noopener noreferrer">LinkedIn</a></li>
              <li><a href={`${PORTAL_URL}/login`}>Team sign in</a></li>
            </ul>
          </div>
          <div>
            <h4>Start a conversation</h4>
            <ul>
              <li><a href="tel:+18137752200">813-775-2200</a></li>
              <li><a href="mailto:info@emergesc.com">info@emergesc.com</a></li>
              <li><a href="#contact">Partnership &amp; referrals</a></li>
            </ul>
          </div>
        </div>
        <div className="footer__bottom">
          <span>&copy; {new Date().getFullYear()} eMerge Senior Care. All rights reserved.</span>
          <span>3000 Bayport Drive, Suite 980, Tampa, FL 33607</span>
        </div>
      </footer>
    </div>
  );
}
